import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import { Button, Tooltip } from "@mui/material";
import { type Issue, SubmissionDocumentType } from "generated-graphql/graphql";
import { type SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { useMemo } from "react";

import { type SubmissionDocumentMetadata } from "./helpers/documentHelpers";

interface SaveAndPrepareForSigningButtonProps {
  signer: SubmissionReviewerSigner | null;
  documents: SubmissionDocumentMetadata[];
  issues: Issue[];
  loading: boolean;
  formLoading: boolean;
  isSubmitting: boolean;
  isValidating: boolean;
  onClick: () => void;
}

export function SaveAndPrepareForSigningButton({
  signer,
  documents,
  issues,
  loading,
  formLoading,
  isSubmitting,
  isValidating,
  onClick,
}: SaveAndPrepareForSigningButtonProps) {
  const prepareForSigningDisabled = useMemo(() => {
    if (!signer) {
      return true;
    }

    if (issues.length > 0) {
      return true;
    }

    if (
      documents.filter((d) => d.submissionDocumentType === SubmissionDocumentType.Submission)
        .length === 0
    ) {
      return true;
    }

    return loading || formLoading || isSubmitting || isValidating;
  }, [documents, issues, formLoading, isSubmitting, isValidating, loading, signer]);

  const tooltipTitle = useMemo(() => {
    if (issues.length > 0) {
      return "Resolve the errors above before preparing for signing";
    }
    if (
      !signer ||
      documents.filter((d) => d.submissionDocumentType === SubmissionDocumentType.Submission)
        .length === 0
    ) {
      return "Signing preparation requires a Signer and Submission Document";
    }
    return "";
  }, [documents, issues, signer]);

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <Button
          variant="contained"
          endIcon={<DrawOutlinedIcon />}
          disabled={prepareForSigningDisabled}
          onClick={onClick}
          sx={{ textTransform: "uppercase" }}
        >
          SAVE & PREPARE FOR SIGNING
        </Button>
      </span>
    </Tooltip>
  );
}
